import React from 'react';
import { useNavigate } from 'react-router-dom';
import CTAButton from '../../components/CTAButton';
import './Industries.css';

const Industries = () => {
  const navigate = useNavigate();
  
  const handleConsultationClick = () => {
    console.log('Consultation button clicked!');
    navigate('/contact');
  };
  
  const industries = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      ),
      title: 'Secure, Scalable Financial Solutions',
      description: 'We build secure, scalable financial solutions designed for the modern economy. From automating compliance with AI-driven KYC onboarding to providing transaction tracking and analytics dashboards, we help you innovate with confidence.',
      solutions: [
        'AI-Powered Fraud Detection',
        'Automated KYC & Onboarding Workflows',
        'Predictive Analytics Dashboards',
        'Secure Digital Wallets & Transaction Tracking'
      ]
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
      ),
      title: 'Intelligent, HIPAA-Compliant Healthcare Apps',
      description: 'Leveraging our expertise in HIPAA-compliant cloud architecture, we build solutions to improve patient care and streamline operations. We use AI to automate appointment management and enable efficient remote consultations, enhancing the patient experience.',
      solutions: [
        'AI-Assisted Appointment Management',
        'Secure, HIPAA-Compliant Cloud Platforms',
        'Streamlined Remote Consultation Tools',
        'Automated Patient Data Processing'
      ]
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
        </svg>
      ),
      title: 'AI-Driven eCommerce Platforms',
      description: 'We create modern online storefronts built for conversion. Our solutions use AI to power personalized recommendations and automate inventory control, while our cloud expertise ensures a mobile-first UI with secure checkout and seamless API integrations.',
      solutions: [
        'AI-Powered Product Recommendation Engines',
        'Automated Inventory Control & Forecasting',
        'Secure Payment & Shipping API Integration',
        'Mobile-First UI on Scalable Cloud Servers'
      ]
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
          <polyline points="9,22 9,12 15,12 15,22"/>
        </svg>
      ),
      title: 'Optimized Booking & Fleet Management',
      description: 'We build real-time booking platforms that use AI to optimize every ride. Our systems feature intelligent driver-user matching, live GPS tracking, and AI-powered dynamic pricing to maximize efficiency and revenue.',
      solutions: [
        'AI Route Optimization & Dynamic Pricing',
        'Real-Time GPS Tracking & Matching Algorithms',
        'Secure In-App Payment Processing',
        'Scalable Cloud Backend for High-Volume Bookings'
      ]
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
          <path d="M6 12v5c3 3 9 3 12 0v-5"/>
        </svg>
      ),
      title: 'Personalized & Interactive Learning Platforms',
      description: 'We build next-generation eLearning platforms that adapt to every student. By leveraging AI, we enable content personalization, real-time assessments, and intelligent student progress tracking to create a more effective and engaging learning environment.',
      solutions: [
        'AI-Driven Content Personalization',
        'Automated Real-Time Student Assessments',
        'Intelligent Progress Tracking & Analytics',
        'Cloud-Based Content Delivery Networks'
      ]
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"/>
          <path d="M8 5V3a2 2 0 012-2h4a2 2 0 012 2v2"/>
        </svg>
      ),
      title: 'Automated & Intelligent Hospitality Solutions',
      description: 'We enable seamless hotel reservations with intelligent, automated systems. Our platforms feature AI-powered smart pricing, real-time availability tracking, and automated guest communication tools, all delivered on a web and mobile-ready cloud platform.',
      solutions: [
        'AI-Powered Smart Pricing Engines',
        'Automated Guest Communication Tools',
        'Real-Time Availability Tracking',
        'Web & Mobile-Ready Cloud Reservation Systems'
      ]
    }
  ];

  return (
    <main>
      {/* Page Header Section */}
      <section className="page-header">
        <div className="page-header-bg">
          <div className="industries-tailored-bg"></div>
        </div>
        <div className="page-header-content">
          <h1 className="page-header-title">Industry Tailored Digital Solutions</h1>
          <p className="page-header-subtitle">
            We apply our expertise in AI and the Cloud to build solutions that solve the core challenges of your industry.
          </p>
        </div>
      </section>

      {/* Industry Showcase Section */}
      <section className="industries-showcase">
        <div className="container">
          <div className="industries-grid">
            {industries.map((industry, index) => (
              <div key={index} className="industry-card">
                <div className="industry-header">
                  <div className="industry-icon">
                    {industry.icon}
                  </div>
                  <h3 className="industry-title">{industry.title}</h3>
                </div>
                <div className="industry-content">
                  <p className="industry-description">{industry.description}</p>
                  <div className="industry-solutions">
                    <h4 className="solutions-heading">Key AI & Cloud Solutions:</h4>
                    <ul className="solutions-list">
                      {industry.solutions.map((solution, solutionIndex) => (
                        <li key={solutionIndex} className="solution-item">
                          <span className="solution-arrow">→</span>
                          {solution}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="industries-cta">
        <div className="container">
          <h2>Don't see your industry?</h2>
          <p>
            Our expertise in AI and Cloud technology is adaptable. Let's discuss how we can build a custom solution for your unique business challenges.
          </p>
          <CTAButton 
            variant="primary" 
            size="large" 
            href="/contact"
            onClick={handleConsultationClick}
          >
            Request a Custom Consultation
          </CTAButton>
        </div>
      </section>
    </main>
  );
};

export default Industries;
